import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../services/apiClient';

// Fleet — the trucks, live from Azuga GPS. Three tabs: where they are right now,
// what each did today, and odometer / service (which also feeds the Equipment Cards).
// Lives on the Schedule and General Information page.

const STATE = {
  moving:    { label: 'Moving',    bg: '#d1fae5', fg: '#065f46', dot: '#16a34a' },
  speeding:  { label: 'Speeding',  bg: '#fee2e2', fg: '#991b1b', dot: '#dc2626' },
  idling:    { label: 'Idling',    bg: '#fef3c7', fg: '#92400e', dot: '#d97706' },
  stopped:   { label: 'Stopped',   bg: '#e5e7eb', fg: '#374151', dot: '#6b7280' },
  no_signal: { label: 'No signal', bg: '#f3f4f6', fg: '#9ca3af', dot: '#d1d5db' },
};
const stateOf = s => STATE[s] || STATE.no_signal;
const clock = iso => { if (!iso) return '—'; const d = new Date(iso); return isNaN(d) ? '—' : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); };
const dur = sec => { if (!sec) return '—'; const m = Math.round(sec / 60); return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`; };
const ago = iso => {
  if (!iso) return '';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 2) return 'just now'; if (m < 60) return `${m} min ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ${m % 60}m ago`;
  return `${Math.floor(m / 1440)}d ago`;
};
const num = n => n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
// Most job names already lead with their number ("2631 - RTC Pyramid Highway").
// What the truck is, beside its number (Ryan, Oct 27 2026): its Equipment Card name when it's linked, else
// Azuga's year / make / model.
const truckDesc = t => {
  const e = t.equipment, v = t.vehicle || {};
  const nm = e && e.name ? String(e.name).replace(String(t.name), '').replace(/^[\s·#—-]+/, '').trim() : '';
  return nm || [v.year, v.make, v.model].filter(Boolean).join(' ');
};
const Desc = ({ t }) => (truckDesc(t) ? <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)', marginTop: 1 }}>{truckDesc(t)}</div> : null);
const jobLabel = j => j.number && !String(j.name).startsWith(String(j.number)) ? `${j.number} — ${j.name}` : j.name;
const where = t => t.at_job ? `At ${jobLabel(t.at_job)}` : t.at_yard ? 'At the Titan yard' : (t.address || 'Location unknown');

const th = { textAlign: 'left', padding: '9px 10px', fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' };
const td = { padding: '10px', fontSize: 13, color: 'var(--text)', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' };
const pill = s => ({ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 700, borderRadius: 999, padding: '3px 10px', background: stateOf(s).bg, color: stateOf(s).fg, whiteSpace: 'nowrap' });
const wrap = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflowX: 'auto' };

export default function FleetBoard({ onBack }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('now');
  const [open, setOpen] = useState(null);      // truck id whose stops are showing
  const [linking, setLinking] = useState('');
  const [probe, setProbe] = useState(null);    // null | 'loading' | what Azuga sent
  const isExec = (() => { try { return ['admin', 'Executive'].includes(JSON.parse(localStorage.getItem('titan_session') || 'null')?.role); } catch { return false; } })();
  const runProbe = () => { setProbe('loading'); apiFetch('/admin/azuga-probe').then(setProbe).catch(e => setProbe({ error: e?.error || e?.message || 'failed' })); };

  const load = useCallback(() => apiFetch('/fleet/summary').then(d => { setData(d); setErr(''); }).catch(e => setErr(e?.error || e?.message || 'Could not load the fleet.')), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  async function link(azugaId, equipmentId) {
    setLinking(azugaId);
    try { await apiFetch('/fleet/link', { method: 'POST', body: JSON.stringify({ azuga_id: azugaId, equipment_id: equipmentId || null }) }); await load(); }
    catch (e) { setErr(e?.error || e?.message || 'Could not save the link.'); }
    finally { setLinking(''); }
  }

  const trucks = data?.trucks || [];
  const c = data?.counts || {};
  const onRoad = (c.moving || 0) + (c.speeding || 0);
  const tabs = [['now', 'Right now'], ['today', 'Today'], ['service', 'Odometer & service']];
  const due = trucks.filter(t => t.equipment?.service_state === 'due').length;

  return (
    <div>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#e87722', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0, marginBottom: 10 }}>‹ Schedule</button>
      <h1 style={{ fontSize: 21, fontWeight: 800, margin: '0 0 4px' }}>Fleet</h1>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 14 }}>
        The trucks, live from Azuga GPS{data?.as_of ? ` · updated ${clock(data.as_of)}` : ''} · refreshes every minute
      </div>

      {err && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{err}</div>}
      {!data && !err && <div style={{ color: 'var(--text-muted)', padding: 30 }}>Loading the trucks…</div>}
      {data && data.configured === false && (
        <div style={{ ...wrap, padding: 22, color: 'var(--text-muted)', fontSize: 14 }}>Azuga isn’t connected on this server, so there’s nothing to show here yet.</div>
      )}

      {data && data.configured !== false && (<>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16, maxWidth: 760 }}>
          {[['On the road', onRoad, '#16a34a'], ['Idling', c.idling || 0, '#d97706'], ['Stopped', c.stopped || 0, '#6b7280'], ['No signal', c.no_signal || 0, '#d1d5db'], ['Service due', due, due ? '#dc2626' : '#d1d5db']].map(([label, n, color]) => (
            <div key={label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderLeft: `4px solid ${color}`, borderRadius: 10, padding: '10px 14px' }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', lineHeight: 1.1 }}>{n}</div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {tabs.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              style={{ padding: '8px 16px', borderRadius: 999, border: '1px solid var(--border)', background: tab === k ? '#111827' : 'var(--surface)', color: tab === k ? '#fff' : 'var(--text)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{label}</button>
          ))}
        </div>

        {trucks.length === 0 && <div style={{ ...wrap, padding: 22, color: 'var(--text-muted)', fontSize: 14 }}>Azuga didn’t send any trucks back{data.errors?.vehicles ? ` — ${data.errors.vehicles}` : ''}.</div>}

        {tab === 'now' && trucks.length > 0 && (
          <div style={wrap}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead><tr><th style={th}>Truck</th><th style={th}>Status</th><th style={th}>Where</th><th style={th}>Driver</th><th style={th}>Last heard</th></tr></thead>
              <tbody>
                {trucks.map(t => (
                  <tr key={t.id}>
                    <td style={{ ...td, fontWeight: 700 }}>{t.name}{t.equipment?.asset_number && !String(t.name).includes(t.equipment.asset_number) ? <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}> · #{t.equipment.asset_number}</span> : null}<Desc t={t} /></td>
                    <td style={td}><span style={pill(t.state)}><span style={{ width: 7, height: 7, borderRadius: '50%', background: stateOf(t.state).dot }} />{stateOf(t.state).label}{(t.state === 'moving' || t.state === 'speeding') && t.speed != null ? ` · ${t.speed} mph` : ''}</span></td>
                    <td style={td}>
                      <div style={{ fontWeight: t.at_job ? 700 : 400 }}>
                        {t.at_job ? <button onClick={() => navigate(`/admin/new-projects/${t.at_job.id}`)} style={{ background: 'none', border: 'none', padding: 0, color: '#1d4ed8', fontWeight: 700, fontSize: 13, cursor: 'pointer', textAlign: 'left' }}>{where(t)}</button> : (t.state === 'no_signal' ? '—' : where(t))}
                      </div>
                      {t.at_job && t.address && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.address}</div>}
                      {t.stopped_since && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Parked since {clock(t.stopped_since)}</div>}
                    </td>
                    <td style={td}>{t.driver || <span style={{ color: '#c4c4c4' }}>—</span>}</td>
                    <td style={{ ...td, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{t.seen_at ? ago(t.seen_at) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'today' && trucks.length > 0 && (<>
          {data.errors?.trips && <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 14px', color: '#92400e', fontSize: 13, marginBottom: 12 }}>Azuga wouldn’t hand over today’s trips — {data.errors.trips}</div>}
          <div style={wrap}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead><tr><th style={th}>Truck</th><th style={th}>First start</th><th style={th}>Last stop</th><th style={{ ...th, textAlign: 'right' }}>Miles</th><th style={{ ...th, textAlign: 'right' }}>Driving</th><th style={{ ...th, textAlign: 'right' }}>Idling</th><th style={{ ...th, textAlign: 'right' }}>Top speed</th><th style={th}>Stops</th></tr></thead>
              <tbody>
                {trucks.map(t => {
                  const d = t.today || {};
                  const quiet = !d.trips;
                  return (
                    <React.Fragment key={t.id}>
                      <tr style={{ opacity: quiet ? 0.5 : 1 }}>
                        <td style={{ ...td, fontWeight: 700 }}>{t.name}<Desc t={t} /></td>
                        <td style={td}>{quiet ? 'Hasn’t moved today' : clock(d.first_start)}</td>
                        <td style={td}>{quiet ? '' : clock(d.last_end)}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{quiet ? '' : num(d.miles)}</td>
                        <td style={{ ...td, textAlign: 'right' }}>{quiet ? '' : dur(d.drive_sec)}</td>
                        <td style={{ ...td, textAlign: 'right', color: d.idle_sec > 3600 ? '#b45309' : 'var(--text)', fontWeight: d.idle_sec > 3600 ? 700 : 400 }}>{quiet ? '' : dur(d.idle_sec)}</td>
                        <td style={{ ...td, textAlign: 'right', color: d.max_mph >= 75 ? '#dc2626' : 'var(--text)', fontWeight: d.max_mph >= 75 ? 700 : 400 }}>{quiet || !d.max_mph ? '' : `${d.max_mph} mph`}</td>
                        <td style={td}>{!quiet && d.stops?.length > 0 && (
                          <button onClick={() => setOpen(open === t.id ? null : t.id)} style={{ background: 'none', border: 'none', padding: 0, color: '#1d4ed8', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>{open === t.id ? '▾' : '▸'} {d.stops.length} stop{d.stops.length === 1 ? '' : 's'}</button>
                        )}</td>
                      </tr>
                      {open === t.id && (
                        <tr><td colSpan={8} style={{ ...td, background: '#f9fafb', paddingLeft: 24 }}>
                          {d.stops.map((s, i) => (
                            <div key={i} style={{ display: 'flex', gap: 12, padding: '3px 0', fontSize: 13 }}>
                              <span style={{ width: 70, flexShrink: 0, fontWeight: 700 }}>{clock(s.at)}</span>
                              <span style={{ flex: 1 }}>{s.address || 'Unknown address'}</span>
                              <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{s.minutes != null ? `stayed ${dur(s.minutes * 60)}` : 'still there'}</span>
                            </div>
                          ))}
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>A trip is one key-on to key-off. A trip still under way shows up once the truck is shut off.</div>
        </>)}

        {tab === 'service' && trucks.length > 0 && (<>
          <div style={wrap}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
              <thead><tr><th style={th}>Truck (Azuga)</th><th style={th}>Equipment card</th><th style={{ ...th, textAlign: 'right' }}>Odometer</th><th style={th}>Next service</th><th style={th}>Also reported</th></tr></thead>
              <tbody>
                {trucks.map(t => {
                  const e = t.equipment, v = t.vehicle || {};
                  const svc = e?.service_state;
                  return (
                    <tr key={t.id}>
                      <td style={{ ...td, fontWeight: 700 }}>{t.name}<div style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)' }}>{[v.year, v.make, v.model].filter(Boolean).join(' ')}{v.plate ? ` · ${v.plate}` : ''}</div></td>
                      <td style={td}>
                        {e ? (
                          <div>
                            <button onClick={() => navigate(`/admin/shop?tab=equipment&equipment=${e.id}`)} style={{ background: 'none', border: 'none', padding: 0, color: '#1d4ed8', fontWeight: 700, fontSize: 13, cursor: 'pointer', textAlign: 'left' }}>{e.asset_number ? `#${e.asset_number} · ` : ''}{e.name}</button>
                            {data.can_link && <button disabled={linking === t.id} onClick={() => link(t.id, null)} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 11.5, cursor: 'pointer', marginLeft: 8 }}>unlink</button>}
                          </div>
                        ) : data.can_link ? (
                          <select disabled={linking === t.id} value="" onChange={ev => ev.target.value && link(t.id, ev.target.value)}
                            style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, maxWidth: 240 }}>
                            <option value="">Link to a truck…</option>
                            {(data.unlinked_equipment || []).map(u => <option key={u.id} value={u.id}>{u.asset_number ? `#${u.asset_number} · ` : ''}{u.name}</option>)}
                          </select>
                        ) : <span style={{ color: '#c4c4c4' }}>Not linked</span>}
                      </td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 700 }}>{num(e?.mismatch ? e.mismatch.azuga : (e?.meter_reading ?? v.odometer))}{(e?.meter_reading ?? v.odometer) != null ? ' mi' : ''}</div>
                        {e?.mismatch && <div style={{ fontSize: 11.5, color: '#b45309', whiteSpace: 'normal', maxWidth: 190, marginLeft: 'auto' }}>Card says {num(e.mismatch.card)} — too far apart to trust, so the card was left alone.</div>}
                        {e && !e.mismatch && e.meter_synced_at && <div style={{ fontSize: 11.5, color: '#16a34a' }}>✓ on the card</div>}
                      </td>
                      <td style={td}>
                        {!e ? <span style={{ color: '#c4c4c4' }}>—</span> : e.next_service_at == null && !e.next_service_due ? <span style={{ color: 'var(--text-muted)' }}>None set on the card</span> : (
                          <div>
                            <span style={{ fontSize: 11.5, fontWeight: 700, borderRadius: 6, padding: '2px 8px', background: svc === 'due' ? '#fee2e2' : svc === 'soon' ? '#fef3c7' : '#d1fae5', color: svc === 'due' ? '#991b1b' : svc === 'soon' ? '#92400e' : '#065f46' }}>
                              {svc === 'due' ? 'SERVICE DUE' : svc === 'soon' ? 'COMING UP' : 'OK'}
                            </span>
                            <div style={{ fontSize: 12.5, marginTop: 3 }}>
                              {e.next_service_at != null && <>at {num(e.next_service_at)} mi{e.miles_to_service != null ? ` (${e.miles_to_service <= 0 ? `${num(-e.miles_to_service)} over` : `${num(e.miles_to_service)} to go`})` : ''}</>}
                              {e.next_service_at != null && e.next_service_due ? ' · ' : ''}{e.next_service_due ? `by ${e.next_service_due}` : ''}
                            </div>
                          </div>
                        )}
                      </td>
                      <td style={{ ...td, color: 'var(--text-muted)', fontSize: 12.5 }}>{[v.engine_hours ? `${num(v.engine_hours)} engine hrs` : null, v.fuel_pct ? `fuel ${v.fuel_pct}%` : null, v.odometer_source === 'device' ? 'odometer from the GPS plug' : null].filter(Boolean).join(' · ') || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8, maxWidth: 760, lineHeight: 1.5 }}>
            A linked truck’s odometer is copied onto its Equipment Card a few times a day (and whenever this page is open), so “service due” flags itself by the miles. It’s only copied when it carries on from what the card already says — never backwards, never a jump of more than 25,000 miles.
          </div>
        </>)}

        {isExec && (
          <div style={{ marginTop: 22 }}>
            <button onClick={runProbe} disabled={probe === 'loading'} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text-muted)', fontSize: 12, textDecoration: 'underline', cursor: 'pointer' }}>
              {probe === 'loading' ? 'Asking Azuga…' : 'Check Azuga data (Executive)'}
            </button>
            {probe && probe !== 'loading' && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Exactly what Azuga sent back (first truck, first trip). If a column above is blank, copy this to whoever is fixing it.</div>
                <textarea readOnly value={JSON.stringify(probe, null, 2)} onFocus={e => e.target.select()}
                  style={{ width: '100%', maxWidth: 760, height: 220, fontFamily: 'monospace', fontSize: 11.5, border: '1px solid var(--border)', borderRadius: 8, padding: 10, boxSizing: 'border-box' }} />
              </div>
            )}
          </div>
        )}
      </>)}
    </div>
  );
}
